import axios from "axios";
import moment from "moment";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilter } from "react-icons/fa";
import { FiCalendar, FiMapPin } from "react-icons/fi";
import { IoIosArrowBack, IoIosClose } from "react-icons/io";
import { MdEdit, MdFlight } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import Book from "../Book/Book";
import "../FlightResult/FlightResult.css";
import Spinner from "../Spinner/Spinner";
import BannerPop from "../BannerPop/BannerPop";
import { toast } from "react-toastify";

const FlightResult = () => {
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [flights, setFlights] = useState([]);
  const [selectedDates, setSelectedDates] = useState({
    from: null,
    to: null,
  });

  const [loading, setLoading] = useState(true);
  const sidebarRef = useRef(null);
  const [selectedAirlines, setSelectedAirlines] = useState([]);
  const [priceRange, setPriceRange] = useState([5000, 15000]);
  const [nonStopOnly, setNonStopOnly] = useState(false);
  const [withStopsOnly, setWithStopsOnly] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showBookComponent, setShowBookComponent] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [animateCards, setAnimateCards] = useState(false);

  // Pagination state variables
  const [visibleFlightsCount, setVisibleFlightsCount] = useState(7);
  const [isExpanded, setIsExpanded] = useState(false);

  const [showBannerPop, setShowBannerPop] = useState(false);
  const bannerTriggeredRef = useRef(false);

  // Filter search states
  const [filterTripType, setFilterTripType] = useState("one-way");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterFromAirportCode, setFilterFromAirportCode] = useState("");
  const [filterToAirportCode, setFilterToAirportCode] = useState("");
  const [filterDepartDate, setFilterDepartDate] = useState(null);
  const [filterReturnDate, setFilterReturnDate] = useState(null);
  const [filterPassengerCount, setFilterPassengerCount] = useState(1);
  const [filterTravelClass, setFilterTravelClass] = useState("Economy");
  const [allAirports, setAllAirports] = useState([]);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [filterOpenDropdown, setFilterOpenDropdown] = useState(null);

  const [fromInputTouched, setFromInputTouched] = useState(false);

  // FIXED: Create searchParams from URL params
  const searchParams = useMemo(() => {
    const params = {
      origin: urlSearchParams.get("origin") || "LON",
      destination: urlSearchParams.get("destination") || "",
      departureDate: urlSearchParams.get("departureDate") || "",
      returnDate: urlSearchParams.get("returnDate") || null,
      travelers: parseInt(urlSearchParams.get("travelers")) || 1,
      tripType: urlSearchParams.get("tripType") || "round-trip",
      travelClass: urlSearchParams.get("travelClass") || "Economy",
      originName: urlSearchParams.get("originName") || "London All Airport",
      destinationName: urlSearchParams.get("destinationName") || "",
    };

    // If no departure date, set default
    if (!params.departureDate) {
      const d = new Date();
      d.setDate(d.getDate() + 15);
      params.departureDate = d.toISOString().split("T")[0];
    }

    // Store in sessionStorage
    sessionStorage.setItem("flightSearchParams", JSON.stringify(params));

    return params;
  }, [urlSearchParams]);

  // Pagination functions
  const handleShowMore = () => {
    setVisibleFlightsCount((prev) => prev + 7);
    setIsExpanded(true);
  };

  const handleShowLess = () => {
    setVisibleFlightsCount(7);
    setIsExpanded(false);
  };

  // Helper function to add one day to a date
  const addOneDay = (date) => {
    if (!date) return null;
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  };

  // Function to update URL parameters
  const updateUrlParams = (newParams) => {
    const newUrlParams = new URLSearchParams();

    // Add all parameters
    if (newParams.origin) newUrlParams.set("origin", newParams.origin);
    if (newParams.destination)
      newUrlParams.set("destination", newParams.destination);
    if (newParams.departureDate)
      newUrlParams.set("departureDate", newParams.departureDate);
    if (newParams.returnDate)
      newUrlParams.set("returnDate", newParams.returnDate);
    if (newParams.travelers)
      newUrlParams.set("travelers", newParams.travelers.toString());
    if (newParams.tripType) newUrlParams.set("tripType", newParams.tripType);
    if (newParams.travelClass)
      newUrlParams.set("travelClass", newParams.travelClass);
    if (newParams.originName)
      newUrlParams.set("originName", newParams.originName);
    if (newParams.destinationName)
      newUrlParams.set("destinationName", newParams.destinationName);

    navigate(`/flight-result?${newUrlParams.toString()}`, { replace: true });
  };

  // FIXED: Initialize filter states from searchParams
  useEffect(() => {
    if (searchParams) {
      console.log(
        "Initializing filter states from searchParams:",
        searchParams,
      );
      setFilterTripType(
        searchParams.tripType === "round-trip" ? "round" : "one-way",
      );
      setFilterFrom(searchParams.originName || "");
      setFilterFromAirportCode(searchParams.origin || "");
      setFilterTo(searchParams.destinationName || "");
      setFilterToAirportCode(searchParams.destination || "");

      // FIXED: Handle date initialization properly
      if (searchParams.departureDate) {
        setFilterDepartDate(new Date(searchParams.departureDate));
      }

      // FIXED: Handle return date initialization
      if (searchParams.returnDate) {
        setFilterReturnDate(new Date(searchParams.returnDate));
      } else {
        setFilterReturnDate(null);
      }

      setFilterPassengerCount(searchParams.travelers || 1);
      setFilterTravelClass(searchParams.travelClass || "Economy");
    }
  }, [searchParams]);

  const fetchLocations = async (query, setSuggestions) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_BASE_URL
        }/airports/search?search=${encodeURIComponent(query)}`,
      );

      if (
        response?.data?.success &&
        Array.isArray(response.data?.data) &&
        response.data?.data?.length > 0
      ) {
        // Slice to top 5 for display
        const limitedSuggestions = response.data.data.slice(0, 5);
        setSuggestions(limitedSuggestions);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      setSuggestions([]);
      const queryLower = query.toLowerCase();
      const filteredAirports = allAirports
        .filter((airport) => {
          const nameMatch = airport.name?.toLowerCase().includes(queryLower);
          const codeMatch = airport.code?.toLowerCase().includes(queryLower);
          const cityMatch = airport.city?.toLowerCase().includes(queryLower);
          const countryMatch = airport.country
            ?.toLowerCase()
            .includes(queryLower);

          return nameMatch || codeMatch || cityMatch || countryMatch;
        })
        .slice(0, 5);
      setSuggestions(filteredAirports);
    }
  };

  // Keep the allAirports fetch for fallback
  useEffect(() => {
    const fetchAllAirports = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/airports/search`,
        );

        if (
          response?.data?.data &&
          Array.isArray(response.data?.data) &&
          response.data?.data?.length > 0
        ) {
          setAllAirports(response?.data?.data || []);

          // Set default "From" value if LON airport exists and not already set
          if (!filterFrom && !fromInputTouched) {
            const lonAirport = response?.data?.data.find(
              (a) => a.code === "LON",
            );
            if (lonAirport) {
              setFilterFrom(lonAirport.name);
              setFilterFromAirportCode("LON");
            }
          }
        }
      } catch (err) {
        console.error("Error fetching airports:", err);
      }
    };

    fetchAllAirports();
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isSidebarOpen]);

  // FIXED: Fetch flights when URL params change
  useEffect(() => {
    const fetchFlights = async () => {
      setLoading(true);
      console.log("Fetching flights with params:", searchParams);

      try {
        const formatDateForAPI = (dateString) => {
          if (!dateString) return null;
          const date = new Date(dateString);
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        };

        const apiParams = {
          departure: searchParams.origin,
          destination: searchParams.destination,
          travelDate: searchParams.departureDate,
          isRoundTrip: searchParams.tripType === "round-trip",
          returnDate: searchParams.returnDate,
        };

        console.log("API params:", apiParams);

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/flight/search`,
          {
            params: apiParams,
          },
        );

        console.log("API response:", response.data);

        setSelectedDates(
          response?.data?.selectedDates ?? { from: null, to: null },
        );

        const transformedFlights = Array.isArray(response?.data?.data)
          ? response?.data?.data?.map((flight, index) => ({
              id: `flight-${index}`,
              flightNo: flight.flightNo,
              airlineCode: flight.airline,
              airlineLogo: `/logos/${flight.airlineImage.toUpperCase()}.webp`,
              departureAirport: flight.departure.airport,
              departureAirportName: flight.departure.name,
              arrivalAirport: flight.arrival.airport,
              arrivalAirportName: flight.arrival.name,
              depTime: flight.departure.time,
              arrTime: flight.arrival.time,
              departureDate: new Date(flight.departure.date).toLocaleDateString(
                "en-GB",
                { day: "2-digit", month: "short" },
              ),
              arrivalDate: new Date(flight.arrival.date).toLocaleDateString(
                "en-GB",
                { day: "2-digit", month: "short" },
              ),
              duration: formatDuration(flight.duration),
              stops: flight.stops,
              price: formatPrice(flight?.price),
              totalPrice:
                searchParams.tripType === "round-trip"
                  ? formatPrice(flight?.totalPrice)
                  : null,
              tripType: flight.return ? "round-trip" : "one-way",
              return: flight.return
                ? {
                    ...flight.return,
                    flightNo: flight.return.flightNo,
                    depTime: flight.return.departure?.time,
                    arrTime: flight.return.arrival?.time,
                  }
                : null,
              returnDepartureDate: flight?.return?.departure?.date
                ? new Date(flight.return.departure.date).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "short",
                    },
                  )
                : "",
              returnArrivalDate: flight?.return?.arrival?.date
                ? new Date(flight.return.arrival.date).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "short",
                    },
                  )
                : "",
            }))
          : [];

        setFlights(transformedFlights);
        console.log("Transformed flights:", transformedFlights.length);

        if (transformedFlights.length === 0) {
          toast.error("No flights found");
        } else {
          const prices = transformedFlights.map((f) => getNumericPrice(f));
          const validPrices = prices.filter((p) => p > 0);
          const minPrice =
            validPrices.length > 0 ? Math.min(...validPrices) : 5000;
          const maxPrice =
            validPrices.length > 0 ? Math.max(...validPrices) : 15000;
          setPriceRange([minPrice, maxPrice]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching flights:", err);

        // Handle specific invalid return date error
        if (
          err.response &&
          err.response.status === 400 &&
          err.response.data.message === "please select the valid return date"
        ) {
          toast.error(err.response.data.message);
          setShowBookComponent(true);
        } else if (err.response) {
          toast.error(`Error: ${err.response.data.message || err.message}`);
        } else {
          toast.error("Failed to fetch flights. Please try again.");
        }

        setLoading(false);
      }
    };

    // Only fetch if we have the required parameters
    if (
      searchParams.origin &&
      searchParams.destination &&
      searchParams.departureDate
    ) {
      fetchFlights();
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => {
      if (bannerTriggeredRef.current) return;

      const flightList = document.querySelector(".flight-list-co");
      if (!flightList) return;

      const rect = flightList.getBoundingClientRect();

      // Trigger when user scrolls INTO the flight list
      if (rect.top < window.innerHeight - 150) {
        bannerTriggeredRef.current = true;

        // optional delay after scroll
        setTimeout(() => {
          setShowBannerPop(true);
        }, 15000);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getNumericPrice = (flight) => {
    const priceToUse =
      searchParams.tripType === "round-trip"
        ? flight?.totalPrice
        : flight?.price;
    if (typeof priceToUse !== "string") return 0;
    const cleaned = priceToUse.replace(/,/g, "");
    return parseInt(cleaned, 10) || 0;
  };

  const formatDuration = (duration) => {
    if (typeof duration === "string" && duration.endsWith("h")) {
      const hours = parseInt(duration.replace("h", ""));
      if (hours > 24) {
        const displayHours = hours % 24;
        const days = Math.floor(hours / 24);
        return days > 0 ? `${days}d ${displayHours}h` : `${hours}h`;
      }
      return duration;
    }
    return duration || "2h 15m";
  };

  const formatPrice = (price) => {
    let numPrice =
      typeof price === "string"
        ? parseFloat(price.replace("£", ""))
        : Number(price);
    if (!isNaN(numPrice)) {
      return Math.round(numPrice).toLocaleString();
    }
    return "7,500";
  };

  const getStopText = (stops) => {
    switch (stops) {
      case 0:
        return "Non-stop";
      case 1:
        return "1 stop";
      default:
        return `${stops} stops`;
    }
  };

  // UPDATED: Debounce function
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // UPDATED: Create debounced version of fetchLocations
  const debouncedFetchLocations = useMemo(
    () => debounce(fetchLocations, 300),
    [],
  );

  // Filter input handlers
  const handleFilterFromChange = (e) => {
    const value = e.target.value;
    setFilterFrom(value);
    setFilterFromAirportCode("");

    if (value.length > 0) {
      setFilterOpenDropdown("from");
      debouncedFetchLocations(value, setFromSuggestions);
    } else {
      setFilterOpenDropdown(null);
      setFromSuggestions([]);
    }
  };

  const handleFilterToChange = (e) => {
    const value = e.target.value;
    setFilterTo(value);

    if (value && filterToAirportCode) {
      if (!value.includes(filterToAirportCode) && !value.includes(filterTo)) {
        setFilterToAirportCode("");
      }
    }

    if (value.length > 0) {
      setFilterOpenDropdown("to");
      debouncedFetchLocations(value, setToSuggestions);
    } else {
      setFilterOpenDropdown(null);
      setToSuggestions([]);
    }
  };

  const handleFilterFromClick = () => {
    if (!fromInputTouched) {
      setFilterFrom("");
      setFilterFromAirportCode("");
      setFromInputTouched(true);
    }

    setFilterOpenDropdown("from");
    if (filterFrom.length > 1) {
      debouncedFetchLocations(filterFrom, setFromSuggestions);
    }
  };

  // Handle To input click
  const handleFilterToClick = () => {
    setFilterOpenDropdown("to");
    if (filterTo.length > 1) {
      debouncedFetchLocations(filterTo, setToSuggestions);
    }
  };

  const handleFilterAirportSelect = (airport, type) => {
    if (type === "departure") {
      setFilterFrom(airport.name);
      setFilterFromAirportCode(airport.code);
      setFromSuggestions([]);
    } else {
      setFilterTo(airport.name);
      setFilterToAirportCode(airport.code);
      setToSuggestions([]);
    }
    setFilterOpenDropdown(null);
  };

  const handleFilterDepartDateChange = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    setFilterDepartDate(adjustedDate);
    if (filterReturnDate && filterReturnDate < adjustedDate) {
      setFilterReturnDate(null);
    }
  };

  const handleFilterReturnDateChange = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    setFilterReturnDate(adjustedDate);
  };

  // FIXED: Swap locations in filter
  const swapFilterLocations = () => {
    const tempFrom = filterFrom;
    const tempFromCode = filterFromAirportCode;
    const tempTo = filterTo;
    const tempToCode = filterToAirportCode;

    setFilterFrom(tempTo);
    setFilterFromAirportCode(tempToCode);
    setFilterTo(tempFrom);
    setFilterToAirportCode(tempFromCode);
  };

  // Apply filters handler
  const applyFilters = () => {
    if (!filterFromAirportCode || !filterToAirportCode) {
      alert("Please select valid departure and arrival airports");
      return;
    }

    if (filterTripType === "round" && !filterReturnDate) {
      alert("Please select a return date");
      return;
    }

    const formatDateForAPI = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const searchData = {
      origin: filterFromAirportCode,
      originName: filterFrom,
      destination: filterToAirportCode,
      destinationName: filterTo,
      departureDate: formatDateForAPI(filterDepartDate),
      returnDate:
        filterTripType === "round" ? formatDateForAPI(filterReturnDate) : null,
      travelers: filterPassengerCount,
      tripType: filterTripType === "round" ? "round-trip" : "one-way",
      travelClass: filterTravelClass,
      fromFullName: `${filterFrom} (${filterFromAirportCode})`,
      toFullName: `${filterTo} (${filterToAirportCode})`,
    };

    console.log("Applying filters with data:", searchData);

    // Update URL parameters
    updateUrlParams(searchData);

    // Keep sessionStorage for backward compatibility
    sessionStorage.setItem("flightSearchParams", JSON.stringify(searchData));

    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Filter handlers
  const handleAirlineSelection = (airline) => {
    setSelectedAirlines((prev) =>
      prev.includes(airline)
        ? prev.filter((a) => a !== airline)
        : [...prev, airline],
    );
  };

  const toggleNonStop = () => {
    setNonStopOnly(!nonStopOnly);
    if (!nonStopOnly) setWithStopsOnly(false);
  };

  const toggleWithStops = () => {
    setWithStopsOnly(!withStopsOnly);
    if (!withStopsOnly) setNonStopOnly(false);
  };

  const resetAllFilters = () => {
    setSelectedAirlines([]);
    setNonStopOnly(false);
    setWithStopsOnly(false);
    setFilterFrom("");
    setFilterTo("");
    setFilterFromAirportCode("");
    setFilterToAirportCode("");
    setFilterDepartDate(null);
    setFilterReturnDate(null);
    setFilterPassengerCount(1);
    setFilterTravelClass("Economy");
    setFromInputTouched(false);

    if (flights?.length > 0) {
      const prices = flights?.map((f) => getNumericPrice(f));
      const validPrices = prices.filter((p) => p > 0);
      const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 5000;
      const maxPrice =
        validPrices.length > 0 ? Math.max(...validPrices) : 15000;
      setPriceRange([minPrice, maxPrice]);
    }
  };

  // Memoized filtered flights
  const filteredFlights = useMemo(() => {
    return (
      flights?.filter((flight) => {
        if (nonStopOnly && flight?.stops !== 0) return false;
        if (withStopsOnly && flight?.stops === 0) return false;

        const numericPrice = getNumericPrice(flight);
        if (numericPrice < priceRange[0] || numericPrice > priceRange[1])
          return false;

        if (
          selectedAirlines.length > 0 &&
          !selectedAirlines.includes(flight?.airline)
        )
          return false;

        return true;
      }) || []
    );
  }, [
    flights,
    nonStopOnly,
    withStopsOnly,
    priceRange,
    selectedAirlines,
    searchParams.tripType,
  ]);

  const shouldShowBackgroundLogo = useMemo(() => {
    return filteredFlights.length < 7;
  }, [filteredFlights.length]);

  useEffect(() => {
    const openBook = urlSearchParams.get("openBook");

    if (openBook === "true" && !showBookComponent) {
      setShowBookComponent(true);

      const newParams = new URLSearchParams(urlSearchParams);
      newParams.delete("openBook");
      setUrlSearchParams(newParams, { replace: true });
    }
  }, [urlSearchParams, setUrlSearchParams, showBookComponent]);

  const toggleBookComponent = () => {
    setShowBookComponent(!showBookComponent);
  };

  const handleNewSearch = (newSearchData) => {
    console.log("New search data:", newSearchData);
    updateUrlParams(newSearchData);

    sessionStorage.setItem("flightSearchParams", JSON.stringify(newSearchData));

    setShowBookComponent(false);
    setAnimateCards(false);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const callAdmin = () => {
    window.location.href = "tel:02033557350  ";
  };

  const handleBookingClick = () => {
    navigate("/booking");
  };

  const FilterCustomInput = forwardRef(({ value, onClick, disabled }, ref) => (
    <div
      className={`filter-custom-date-input ${disabled ? "disabled" : ""}`}
      onClick={!disabled ? onClick : undefined}
      ref={ref}
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <FiCalendar className="filter-date-icon" />
      <span className="filter-date-value">
        {value
          ? new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : "Select"}
      </span>
    </div>
  ));

  if (loading) {
    return (
      <div className="flight-result-co">
        <div className="loading-container-co">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flight-result-co">
      {/* Main Header */}
      <div className="flight-header-co animate-slide-down">
        {isMobile ? (
          <div className="header-actions-mobile">
            <IoIosArrowBack
              className="back-icon-co"
              onClick={() => navigate(-1)}
            />
            <FaFilter
              className="filter-icon-mobile-co"
              onClick={toggleSidebar}
            />
          </div>
        ) : (
          <IoIosArrowBack
            className="back-icon-co"
            onClick={() => navigate(-1)}
          />
        )}
        <div className="d-d-co">
          <h2 className="animate-fade-in">
            {searchParams.origin} → {searchParams.destination}
          </h2>
          <p className="animate-fade-in-delay" onClick={toggleBookComponent}>
            <strong>Depart:</strong>{" "}
            {searchParams.departureDate
              ? new Date(searchParams.departureDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  },
                )
              : "Not selected"}
            {searchParams.returnDate && (
              <>
                {" "}
                | <strong>Return:</strong>{" "}
                {new Date(searchParams.returnDate).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </>
            )}{" "}
            | {searchParams.travelers} Traveller
            {searchParams.travelers > 1 ? "s" : ""}
            {searchParams.travelClass && ` | ${searchParams.travelClass}`}
          </p>

          <button className="search-icon-co" onClick={toggleBookComponent}>
            Modify Search
            <MdEdit />
          </button>
        </div>
      </div>
      {/* Book Component Overlay */}
      {showBookComponent && (
        <div
          className={`book-wrapper-co ${
            showBookComponent ? "show-co animate-fade-in" : ""
          }`}
        >
          <div className="book-header-co">
            <IoIosClose
              className="book-close-icon-co"
              onClick={toggleBookComponent}
            />
          </div>
          <Book onSearch={handleNewSearch} initialSearchParams={searchParams} />
        </div>
      )}

      {/* Main Content */}
      <div className="main-content-co">
        {/* Sidebar Filter */}
        <div
          ref={sidebarRef}
          className={`sidebar-co ${isSidebarOpen ? "open-co" : ""} ${
            isMobile ? "mobile-sidebar" : ""
          }`}
        >
          {isMobile && (
            <div className="sidebar-mobile-header">
              <h3>Filters</h3>
              <IoIosClose
                className="close-icon-mobile-co"
                onClick={closeSidebar}
              />
            </div>
          )}

          <div className="available-container-co animate-fade-in">
            <h4>Flights Available</h4>
            <p
              className={`results-count ${
                filteredFlights?.length > 0 ? "has-results" : ""
              }`}
            >
              {filteredFlights?.length} flights Found
            </p>
          </div>

          {/* 1. Trip Type Filter */}
          <div className="filter-section-fll-co animate-fade-in">
            <h4>Trip Type</h4>
            <div className="filter-trip-type">
              <div className="filter-radio-option">
                <input
                  type="radio"
                  id="filter-one-way"
                  name="filter-tripType"
                  checked={filterTripType === "one-way"}
                  onChange={() => setFilterTripType("one-way")}
                />
                <label htmlFor="filter-one-way">One Way</label>
              </div>
              <div className="filter-radio-option">
                <input
                  type="radio"
                  id="filter-round-trip"
                  name="filter-tripType"
                  checked={filterTripType === "round"}
                  onChange={() => setFilterTripType("round")}
                />
                <label htmlFor="filter-round-trip">Round Trip</label>
              </div>
            </div>
          </div>

          {/* 2. Route Filter */}
          <div className="filter-section-fll-co animate-slide-right">
            <h4>Route</h4>
            <div className="filter-location-group">
              <div className="filter-input-wrapper">
                <div className="filter-input-header">
                  <FiMapPin className="filter-input-icon" />
                  <span className="filter-input-label">From</span>
                  {filterFromAirportCode && (
                    <span className="filter-code-display">
                      {filterFromAirportCode}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={filterFrom}
                  onChange={handleFilterFromChange}
                  onClick={handleFilterFromClick}
                  placeholder="City or airport"
                  className="filter-location-input"
                />
                {filterOpenDropdown === "from" &&
                  fromSuggestions.length > 0 && (
                    <div className="filter-suggestions-dropdown">
                      {fromSuggestions.map((airport) => (
                        <div
                          key={airport.id || airport.code}
                          className="filter-suggestion-item"
                          onClick={() =>
                            handleFilterAirportSelect(airport, "departure")
                          }
                        >
                          <div className="filter-suggestion-details">
                            <div className="filter-suggestion-name">
                              {airport.name}
                            </div>
                            <div className="filter-suggestion-code">
                              {airport.code}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div className="filter-input-wrapper">
                <div className="filter-input-header">
                  <FiMapPin className="filter-input-icon" />
                  <span className="filter-input-label">To</span>
                  {filterToAirportCode && (
                    <span className="filter-code-display">
                      {filterToAirportCode}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={filterTo}
                  onChange={handleFilterToChange}
                  onClick={handleFilterToClick}
                  placeholder="City or airport"
                  className="filter-location-input"
                />
                {filterOpenDropdown === "to" && toSuggestions.length > 0 && (
                  <div className="filter-suggestions-dropdown">
                    {toSuggestions.map((airport) => (
                      <div
                        key={airport.id || airport.code}
                        className="filter-suggestion-item"
                        onClick={() =>
                          handleFilterAirportSelect(airport, "arrival")
                        }
                      >
                        <div className="filter-suggestion-details">
                          <div className="filter-suggestion-name">
                            {airport.name}
                          </div>
                          <div className="filter-suggestion-code">
                            {airport.code}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="filter-section-fll-co animate-slide-right-delay">
            <h4>Dates</h4>
            <div className="filter-dates-group">
              <div className="filter-date-input-wrapper">
                <div className="filter-input-header">
                  <FiCalendar className="filter-input-icon" />
                  <span className="filter-input-label">Depart</span>
                </div>
                <DatePicker
                  selected={filterDepartDate}
                  onChange={handleFilterDepartDateChange}
                  dateFormat="MMM dd, yyyy"
                  customInput={<FilterCustomInput />}
                  popperPlacement="bottom-start"
                  showPopperArrow={false}
                />
              </div>

              {filterTripType === "round" && (
                <div className="filter-date-input-wrapper">
                  <div className="filter-input-header">
                    <FiCalendar className="filter-input-icon" />
                    <span className="filter-input-label">Return</span>
                  </div>
                  <DatePicker
                    selected={filterReturnDate}
                    onChange={handleFilterReturnDateChange}
                    dateFormat="MMM dd, yyyy"
                    minDate={
                      filterDepartDate ? addOneDay(filterDepartDate) : null
                    }
                    disabled={!filterDepartDate}
                    customInput={
                      <FilterCustomInput disabled={!filterDepartDate} />
                    }
                    popperPlacement="bottom-start"
                    showPopperArrow={false}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="help-desk-box-co animate-fade-in-delay">
            <h4>Not sure about your flight details?</h4>
            <div className="help-desk-content-co">
              <img src="/call.webp" className="call-girl" alt="calling-girl" />
              <div className="call-info-co" onClick={callAdmin}>
                <p>02033557350 </p>
              </div>
            </div>
          </div>

          <div className="filter-action-buttons">
            <button className="apply-btn" onClick={applyFilters}>
              Apply
            </button>
            <button className="reset-btn" onClick={resetAllFilters}>
              Reset
            </button>
          </div>
        </div>

        {isMobile && isSidebarOpen && (
          <div className="sidebar-overlay" onClick={closeSidebar} />
        )}

        <div
          className={`content-div-co ${
            shouldShowBackgroundLogo ? "with-background-logo" : ""
          }`}
        >
          <div className="flight-list-co">
            {filteredFlights.length > 0 ? (
              <>
                {/* Show only the first visibleFlightsCount flights */}
                {filteredFlights
                  .slice(0, visibleFlightsCount)
                  .map((flight, index) => (
                    <div
                      key={flight.id}
                      className={`flight-card-co ${
                        animateCards ? "animate-card" : ""
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flight-info-co">
                        <div className="upper-fl-ls">
                          <div className="flight-date">
                            <span className="label">DEPART</span>
                            <span className="date dn">{flight.flightNo}</span>
                          </div>

                          <div className="flight-date">
                            <span className="date">
                              {moment(
                                flight?.departure?.date ||
                                  searchParams.departureDate,
                              )
                                .format("ddd, DD MMM, YYYY")
                                .toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div
                          className={`fd-co ${searchParams.tripType === "one-way" ? "fd-mo" : ""}`}
                        >
                          <div className="airline-info-co">
                            {/* FIXED: Show flight number as fallback when no airline logo found */}
                            <img
                              src={flight.airlineLogo}
                              alt={flight.airline}
                              className="airline-logo-co"
                              onError={(e) => {
                                e.target.onerror = null;
                                // Replace image with flight number
                                const parent = e.target.parentNode;
                                e.target.style.display = "none";
                                const flightNoSpan =
                                  document.createElement("span");
                                flightNoSpan.className =
                                  "airline-logo-fallback";
                                flightNoSpan.textContent =
                                  flight.flightNo || "XXX";
                                parent.insertBefore(
                                  flightNoSpan,
                                  e.target.nextSibling,
                                );
                              }}
                            />
                            <span className="airline-code-co">
                              {flight.airline}
                            </span>
                          </div>

                          <div className="flight-time-info-co">
                            <p className="airport-code-co">
                              <span className="airport-name">
                                {flight?.departureAirportName
                                  ? flight.departureAirportName
                                      .charAt(0)
                                      .toUpperCase() +
                                    flight.departureAirportName
                                      .slice(1)
                                      .toLowerCase()
                                  : ""}
                              </span>
                              <span className="airport-code">
                                {" "}
                                (
                                {(
                                  flight?.departureAirport ||
                                  searchParams.origin ||
                                  ""
                                ).toUpperCase()}
                                )
                              </span>
                            </p>
                            <p className="flight-time-co">
                              {flight.depTime
                                ? flight.depTime
                                    .split(":")
                                    .slice(0, 2)
                                    .join(":")
                                : ""}
                            </p>

                            <p className="date-co">{flight.departureDate}</p>
                          </div>

                          <div className="flight-duration-co">
                            <div className="flight-line-co">
                              <div className="stop-indicator-co">
                                <span className="stop-do-co">
                                  <MdFlight className="flogo-co" />
                                </span>
                              </div>
                            </div>
                            <p className="non-stop-co">
                              {getStopText(flight.stops)}
                            </p>
                          </div>

                          <div className="flight-time-info-co">
                            <p className="airport-code-co">
                              <span className="airport-name">
                                {flight?.arrivalAirportName
                                  ? flight.arrivalAirportName
                                      .charAt(0)
                                      .toUpperCase() +
                                    flight.arrivalAirportName
                                      .slice(1)
                                      .toLowerCase()
                                  : ""}
                              </span>
                              <span className="airport-code">
                                {" "}
                                (
                                {(
                                  flight?.arrivalAirport ||
                                  searchParams.destination ||
                                  ""
                                ).toUpperCase()}
                                )
                              </span>
                            </p>
                            <p className="flight-time-co">
                              {flight.arrTime
                                ? flight.arrTime
                                    .split(":")
                                    .slice(0, 2)
                                    .join(":")
                                : ""}
                            </p>

                            <p className="date-co">{flight.arrivalDate}</p>
                          </div>
                        </div>

                        {searchParams.tripType === "round-trip" &&
                          flight.return && (
                            <div className="flight-info-co return-flight fdd-co">
                              <div className="upper-fl-return">
                                <div className="flight-date">
                                  <span className="label">RETURN </span>
                                  <span className="date dn">
                                    {flight?.return?.flightNo || ""}
                                  </span>
                                </div>
                                <div className="flight-date">
                                  <span className="date">
                                    {moment(flight?.return?.departure?.date)
                                      .format("ddd, DD MMM, YYYY")
                                      .toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="fd-co ">
                                <div className="airline-info-co">
                                  {/* FIXED: Show return flight number as fallback when no airline logo found */}
                                  <img
                                    src={flight.airlineLogo}
                                    alt={flight.airline}
                                    className="airline-logo-co"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      // Replace image with return flight number
                                      const parent = e.target.parentNode;
                                      e.target.style.display = "none";
                                      const flightNoSpan =
                                        document.createElement("span");
                                      flightNoSpan.className =
                                        "airline-logo-fallback";
                                      flightNoSpan.textContent =
                                        flight?.return?.flightNo ||
                                        flight.flightNo ||
                                        "XXX";
                                      parent.insertBefore(
                                        flightNoSpan,
                                        e.target.nextSibling,
                                      );
                                    }}
                                  />
                                  <span className="airline-code-co">
                                    {flight.airline}
                                  </span>
                                </div>

                                <div className="flight-time-info-co">
                                  <p className="airport-code-co">
                                    <span className="airport-name">
                                      {flight?.return?.departure?.name
                                        ? flight.return?.departure?.name
                                            .charAt(0)
                                            .toUpperCase() +
                                          flight.return?.departure?.name
                                            .slice(1)
                                            .toLowerCase()
                                        : ""}
                                    </span>
                                    <span className="airport-code">
                                      {" "}
                                      (
                                      {(
                                        flight?.return?.departure?.airport ||
                                        searchParams.destination ||
                                        ""
                                      ).toUpperCase()}
                                      )
                                    </span>
                                  </p>
                                  <p className="flight-time-co">
                                    {flight?.return?.depTime
                                      ? flight.return.depTime
                                          .split(":")
                                          .slice(0, 2)
                                          .join(":")
                                      : ""}
                                  </p>
                                  <p className="date-co">
                                    {flight.returnDepartureDate}
                                  </p>
                                </div>

                                <div className="flight-duration-co">
                                  <div className="flight-line-co">
                                    <div className="stop-indicator-co">
                                      <span className="stop-do-co">
                                        <MdFlight className="flogoo-co" />
                                      </span>
                                    </div>
                                  </div>
                                  <p className="dura-co">
                                    {flight.return.duration}
                                  </p>
                                  <p className="non-stop-co">
                                    {getStopText(flight.return.stops)}
                                  </p>
                                </div>

                                <div className="flight-time-info-co">
                                  <p className="airport-code-co">
                                    <span className="airport-name">
                                      {flight?.return?.arrival?.name
                                        ? flight.return?.arrival?.name
                                            .charAt(0)
                                            .toUpperCase() +
                                          flight.return?.arrival?.name
                                            .slice(1)
                                            .toLowerCase()
                                        : ""}
                                    </span>
                                    <span className="airport-code">
                                      {" "}
                                      (
                                      {(
                                        flight?.return?.arrival?.airport ||
                                        searchParams.origin ||
                                        ""
                                      ).toUpperCase()}
                                      )
                                    </span>
                                  </p>
                                  <p className="flight-time-co">
                                    {flight?.return?.arrTime
                                      ? flight.return.arrTime
                                          .split(":")
                                          .slice(0, 2)
                                          .join(":")
                                      : ""}
                                  </p>
                                  <p className="date-co">
                                    {flight.returnArrivalDate}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                      </div>

                      <div className="price-div-co">
                        <div className="trip-type-tag-co">
                          {searchParams.tripType === "round-trip"
                            ? "Round Trip"
                            : "One Way"}
                        </div>
                        <p className="flight-price-co">
                          £
                          {searchParams.tripType === "round-trip"
                            ? flight?.totalPrice || flight?.price
                            : flight?.price}
                        </p>

                        <button className="button-42-co" onClick={callAdmin}>
                          Call Now <br />
                          02033557350
                        </button>
                        <button className="button-42-co" onClick={callAdmin}>
                          Unlock Lower <br />
                          'Phone-Only' Fare
                        </button>
                      </div>
                    </div>
                  ))}

                {/* Pagination controls in a single div */}
                <div className="pagination-controls">
                  {/* Buttons Container */}
                  <div className="pagination-buttons-container">
                    {/* Show both buttons when expanded */}
                    {isExpanded && visibleFlightsCount > 7 ? (
                      <div className="both-buttons">
                        <button
                          className="explore-more-btn"
                          onClick={handleShowMore}
                        >
                          Explore more (
                          {filteredFlights.length - visibleFlightsCount} more
                          flights)
                        </button>
                        <button
                          className="see-less-btn"
                          onClick={handleShowLess}
                        >
                          See less
                        </button>
                      </div>
                    ) : (
                      /* Show only Explore more button when not expanded */
                      filteredFlights.length > visibleFlightsCount && (
                        <div className="single-button">
                          <button
                            className="explore-more-btn"
                            onClick={handleShowMore}
                          >
                            Explore more (
                            {filteredFlights.length - visibleFlightsCount} more
                            flights)
                          </button>
                        </div>
                      )
                    )}
                  </div>

                  {/* Banner image with phone number - below buttons */}
                  <div className="phone-banner-co" onClick={callAdmin}>
                    <img
                      src="/banner2.webp"
                      alt="Call for Special Offers"
                      className="banner-image-co"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="no-results-co animate-fade-in">
                <div className="no-results-icon">✈️</div>
                <button className="reset-btn-co" onClick={toggleBookComponent}>
                  Select Your Journey Date
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isMobile &&
        !isSidebarOpen &&
        filteredFlights.length > 0 &&
        !showBookComponent && (
          <button
            className="mobile-filter-fab animate-bounce"
            onClick={toggleSidebar}
          >
            <FaFilter />
            {selectedAirlines.length > 0 && (
              <span className="filter-badge">{selectedAirlines.length}</span>
            )}
          </button>
        )}
      <div className="price-info-bar">
        <div className="price-info-item">
          ✓ Prices are subject to seat availability.
        </div>
        <div className="price-info-item">
          ✓ Prices were last updated 5 hours ago
        </div>
        <div className="price-info-item">✓ Prices are subject to change</div>
        <div className="price-info-item">
          ✓ Prices have limited seat availability
        </div>
      </div>
      <BannerPop
        isOpen={showBannerPop}
        onClose={() => setShowBannerPop(false)}
      />
    </div>
  );
};

export default FlightResult;
